import { useCallback, useEffect, useState } from "react";

import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import TableBody from "@mui/material/TableBody";
import Typography from "@mui/material/Typography";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";

// import { users } from 'src/_mock/user';

// import Scrollbar from 'src/components/scrollbar';

import TableNoData from "../table-no-data";
import UserTableRow from "../user-table-row";
import UserTableHead from "../user-table-head";
import TableEmptyRows from "../table-empty-rows";
import UserTableToolbar from "../user-table-toolbar";
import { emptyRows, applyFilter, getComparator } from "../utils";
import Iconify from "../../../../components/iconify/iconify";
import { useNavigate } from "react-router-dom";
import tributeServices from "../../../../services/tributeServices";
import * as tributeCommentServices from "../../../../services/tributeCommentServices";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../../utils";
import { TableHeaderStyle } from "../../../../theme/palette";

// ----------------------------------------------------------------------


export default function Tributes() {
  const [page, setPage] = useState(0);

  const [order, setOrder] = useState("asc");

  const [selected, setSelected] = useState([]);

  const [orderBy, setOrderBy] = useState("name");

  const [filterName, setFilterName] = useState("");

  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [data, setData] = useState([]);
  const [isArchived, setIsArchived] = useState(false);

  // Comments dialog: tributeId -> list of comments (mock; replace with API)
  const [tributeComments, setTributeComments] = useState({});
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [selectedTributeForComments, setSelectedTributeForComments] = useState(null);
  const [commentIdToDelete, setCommentIdToDelete] = useState(null);
  const [commentsDialogLoading, setCommentsDialogLoading] = useState(false);

  const navigate = useNavigate();

  const handleSort = (event, id) => {
    const isAsc = orderBy === id && order === "asc";
    if (id !== "") {
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(id);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = data?.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const dataFiltered = applyFilter({
    inputData: data,
    comparator: getComparator(order, orderBy),
    filterName,
  });

  const notFound = !dataFiltered.length && !!filterName;
  const getData = useCallback(() => {
    const request = isArchived
      ? tributeServices.getArchived()
      : tributeServices.get();
    request
      .then((res) => {
        setData(res?.data?.data || []);
      })
      .catch((err) => {
        toast.error(getErrorMessage(err));
      });
  }, [isArchived]);

  useEffect(() => {
    getData();
  }, [getData]);


  const handleDeleteRow = (row) => {
    tributeServices.deleteTribute(row._id).then(() => {
      toast.success("Deleted")
      getData();
    }).catch((err) => {
      toast.error(getErrorMessage(err))
    });
  }

  const handleEditRow = (row) => {
    navigate(`EditTribute/${row._id}`);
  };

  const handleArchiveRow = (row) => {
    tributeServices.archiveTribute(row._id).then(() => {
      toast.success("Archived");
      getData();
    }).catch((err) => {
      toast.error(getErrorMessage(err));
    });
  };

  const handleRestoreRow = (row) => {
    tributeServices.restoreTribute(row._id).then(() => {
      toast.success("Restored");
      getData();
    }).catch((err) => {
      toast.error(getErrorMessage(err));
    });
  };

  const handleTabChange = (event, value) => {
    setPage(0);
    setSelected([]);
    setIsArchived(value === "archived");
  };

  const handleViewComments = (row) => {
    setSelectedTributeForComments(row);
    setCommentsDialogOpen(true);
    setCommentsDialogLoading(true);
    tributeCommentServices
      .getCommentsAdmin(row._id)
      .then((res) => {
        setTributeComments((prev) => ({ ...prev, [row._id]: res.data || [] }));
      })
      .catch(() => {
        setTributeComments((prev) => ({ ...prev, [row._id]: [] }));
        toast.error("Failed to load comments.");
      })
      .finally(() => setCommentsDialogLoading(false));
  };

  const handleCloseCommentsDialog = () => {
    setCommentsDialogOpen(false);
    setSelectedTributeForComments(null);
  };

  const handleConfirmDeleteComment = (commentId) => {
    setCommentIdToDelete(commentId);
  };

  const handleCloseDeleteConfirm = () => {
    setCommentIdToDelete(null);
  };

  const handleDeleteComment = () => {
    if (!selectedTributeForComments || !commentIdToDelete) return;
    const tributeId = selectedTributeForComments._id;
    tributeCommentServices
      .deleteCommentAdmin(tributeId, commentIdToDelete)
      .then(() => {
        setTributeComments((prev) => ({
          ...prev,
          [tributeId]: (prev[tributeId] || []).filter((c) => c.id !== commentIdToDelete),
        }));
        setCommentIdToDelete(null);
        toast.success("Comment deleted");
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const commentsForSelected = selectedTributeForComments
    ? (tributeComments[selectedTributeForComments._id] || [])
    : [];

  return (
    <div>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={5}
      >
        <Stack>
          <Typography variant="h4">Tributes</Typography>
          <div style={{
            backgroundColor: TableHeaderStyle.border,
            height: "2px"
          }} />
        </Stack>


        <Button
          variant="contained"
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={() => navigate("CreateTribute")}
          sx={{ backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' }, fontWeight: 'bold' }}
        >
          New Tribute
        </Button>
      </Stack>

      <Card>
        <Tabs
          value={isArchived ? "archived" : "active"}
          onChange={handleTabChange}
          aria-label="Tribute status"
        >
          <Tab value="active" label="Active" />
          <Tab value="archived" label="Archived" />
        </Tabs>

        <UserTableToolbar
          numSelected={selected.length}
          filterName={filterName}
          onFilterName={handleFilterByName}
        />

        <TableContainer sx={{ overflow: "unset" }}>
          <Table sx={{ minWidth: 800 }}>
            <UserTableHead
              order={order}
              orderBy={orderBy}
              rowCount={data?.length}
              numSelected={selected.length}
              onRequestSort={handleSort}
              onSelectAllClick={handleSelectAllClick}
              headLabel={[
                { id: "#", label: "#", width: "5%" },
                { id: "shortStatement", label: "short description" },
                { id: "description", label: "Description" },
                { id: "image", label: "Image" },
                { id: "comments", label: "Comments" },
                { id: "" },
              ]}
            />
            <TableBody>
              {dataFiltered
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => (
                  <UserTableRow
                    key={row._id}
                    rowNumber={page * rowsPerPage + index + 1}
                    shortStatement={row.shortStatement}
                    description={row.description}
                    image={row.image}
                    commentsCount={(tributeComments[row._id] || []).length}
                    onViewComments={() => handleViewComments(row)}
                    isArchived={isArchived}
                    onDelete={() => handleDeleteRow(row)}
                    onEdit={() => handleEditRow(row)}
                    onArchive={() => handleArchiveRow(row)}
                    onRestore={() => handleRestoreRow(row)}
                  />
                ))}

              <TableEmptyRows
                height={77}
                emptyRows={emptyRows(page, rowsPerPage, data?.length)}
              />

              {notFound && <TableNoData query={filterName} />}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          page={page}
          component="div"
          count={dataFiltered.length}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          rowsPerPageOptions={[5, 10, 25]}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      <Dialog
        open={commentsDialogOpen}
        onClose={handleCloseCommentsDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 0, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Box>
            Comments
            {selectedTributeForComments && (
              <Typography component="span" variant="body2" color="text.secondary" fontWeight={400} sx={{ display: "block", mt: 0.5 }}>
                {selectedTributeForComments.shortStatement?.slice(0, 60)}
                {(selectedTributeForComments.shortStatement?.length || 0) > 60 ? "…" : ""}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={handleCloseCommentsDialog}
            aria-label="Close"
            sx={{
              mt: -0.5,
              mr: -0.5,
              color: "inherit",
              "&:hover": {
                backgroundColor: "transparent",
                color: "inherit",
                opacity: 1,
              },
            }}
          >
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {commentsDialogLoading ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography color="text.secondary">Loading comments…</Typography>
            </Box>
          ) : commentsForSelected.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography color="text.secondary">No comments yet.</Typography>
            </Box>
          ) : (
            <Box sx={{ py: 1 }}>
              {commentsForSelected.map((c) => (
                <Box
                  key={c.id}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1,
                    p: 2,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {c.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {c.email}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {new Date(c.createdAt).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {c.text}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => handleConfirmDeleteComment(c.id)}
                    sx={{ color: "error.main", flexShrink: 0, textTransform: "none" }}
                  >
                    Delete
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!commentIdToDelete}
        onClose={handleCloseDeleteConfirm}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete comment?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this comment? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDeleteConfirm} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteComment}
            sx={{
              textTransform: "none",
              backgroundColor: "#1a80c8",
              "&:hover": { backgroundColor: "#1565a0" },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
